<script lang="ts">
import type { RenderFunction } from 'vue';
import type { ThemeSlots } from '#layers/ui/types/tv';
import type { BadgeProps, ButtonProps } from '#ui/types';
import { tv } from 'tailwind-variants';

export const secondaryHeader = tv({
	slots: {
		title: 'flex items-center gap-3 font-display text-xl font-medium text-default',
		root: 'flex min-h-(--secondary-header-height) w-full shrink-0 items-center justify-between gap-6',
		titleContainer: 'flex min-w-0 flex-1 items-center gap-4',
		titleWrapper: 'flex flex-col',
		description: 'max-w-2xl text-sm text-muted',
		icon: '',
		actions: 'flex shrink-0 items-center gap-2',
	},
	variants: {
		bar: {
			true: {
				root: 'border-b px-page py-0 block-(--secondary-header-height)',
			},
		},
		stackOnMobile: {
			true: {
				root: 'flex-col items-stretch gap-4 sm:flex-row sm:items-center sm:gap-6',
				description: 'max-w-none sm:max-w-2xl',
			},
		},
		// Offsets the scroll target so an anchored header isn't flush against the
		// scroll container's top edge when reached via #<id>.
		anchored: {
			true: {
				root: 'scroll-mt-6',
			},
		},
	},
});

export interface SecondaryHeaderActionButton extends ButtonProps {}
export type SecondaryHeaderAction = SecondaryHeaderActionButton | RenderFunction;

export interface SecondaryHeaderProps {
	title?: string;
	description?: string;
	icon?: string;
	iconColor?: string;
	badge?: BadgeProps;
	actions?: SecondaryHeaderAction[];
	stackOnMobile?: boolean;
	bar?: boolean;
	class?: string;
	ui?: ThemeSlots<typeof secondaryHeader>;
	actionsId?: string;
	/** Renders as the root element `id`, making the header targetable via `#<id>`. */
	anchor?: string;
}

export interface SecondaryHeaderSlots {
	'default'?: () => void;
	'icon'?: () => void;
	'title'?: () => void;
	'title-leading'?: () => void;
	'title-trailing'?: () => void;
	'description'?: () => void;
	'actions'?: () => void;
}
</script>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<SecondaryHeaderProps>();

defineSlots<SecondaryHeaderSlots>();

const ui = computed(() => secondaryHeader({ stackOnMobile: props.stackOnMobile, anchored: !!props.anchor, bar: props.bar }));
</script>

<template>
	<div :id="anchor" :class="ui.root({ class: props.class })">
		<div :class="ui.titleContainer({ class: props.ui?.titleContainer })">
			<slot name="title-leading" />
			<slot>
				<slot name="icon">
					<DIconFrame v-if="icon" class="size-7" :color="iconColor" :name="icon" />
				</slot>
				<div :class="ui.titleWrapper({ class: props.ui?.titleWrapper })">
					<h1 :class="ui.title({ class: props.ui?.title })">
						<span class="truncate leading-snug">
							<slot name="title">
								{{ title }}
							</slot>
						</span>
					</h1>
					<p v-if="description || !!$slots.description" :class="ui.description({ class: props.ui?.description })">
						<slot name="description">
							{{ description }}
						</slot>
					</p>
				</div>
			</slot>
			<slot name="title-trailing" />
		</div>
		<div :id="actionsId" :class="ui.actions({ class: props.ui?.actions })">
			<slot name="actions">
				<template v-for="(action, index) in actions" :key="index">
					<component :is="action" v-if="typeof action === 'function'" />
					<DButton v-else v-bind="action">
						{{ action.label }}
					</DButton>
				</template>
			</slot>
		</div>
	</div>
</template>
